var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useFetch } from './useFetch';
import { useConfig } from '../context/ConfigContext';
import { useEventComposerGlobalContext } from '../composers/EventComposerGlobalContext';
import { getRecurrenceKey } from '../components/RecurrenceDropdown/utils';
import { AVAILABILITY_DEBOUNCE_MS, AVAILABILITY_RECURRING_DEBOUNCE_MS, MAX_INVITEES_FOR_AVAILABILITY, } from '../constants/inviteeAvailability';
import { fetchInviteesAvailability } from '../api/userFreeBusy';
import { buildAvailabilityTimeFetchKey, diffInviteeEmails, filterConflictsForEditingEvent, getAvailabilityBounds, getInviteeEmailsForAvailability, inviteeEmailSetsEqual, isAvailabilityWindowWithinLimit, } from '../utils/inviteeAvailability';
import { isAbortError } from '../utils/utils';
import { ComposerMode } from '../types/component';
export var useInviteeAvailability = function (_a) {
    var hideInvitees = _a.hideInvitees;
    var _b = useConfig(), accessToken = _b.accessToken, environmentUrls = _b.environmentUrls;
    var post = useFetch(accessToken).post;
    var _c = useEventComposerGlobalContext(), timezone = _c.timezone, composerMode = _c.composerMode, onError = _c.onError;
    var _d = __read(useWatch({
        name: [
            'invitees',
            'startTime',
            'endTime',
            'recurrenceRule',
            'eventId',
            'uid',
            'isAllDayEvent',
        ],
    }), 7), invitees = _d[0], startTime = _d[1], endTime = _d[2], recurrenceRule = _d[3], eventId = _d[4], uid = _d[5], isAllDayEvent = _d[6];
    var _e = __read(useState(new Map()), 2), conflictsByEmail = _e[0], setConflictsByEmail = _e[1];
    var _f = __read(useState(false), 2), isLoading = _f[0], setIsLoading = _f[1];
    var _g = __read(useState(false), 2), isAvailabilitySkipped = _g[0], setIsAvailabilitySkipped = _g[1];
    var debounceRef = useRef(null);
    var abortControllerRef = useRef(null);
    var previousTimeFetchKeyRef = useRef('');
    var previousEmailsRef = useRef(new Set());
    var forceFullFetchRef = useRef(false);
    var recurrenceKey = getRecurrenceKey(recurrenceRule);
    var currentEmails = useMemo(function () { return getInviteeEmailsForAvailability(invitees !== null && invitees !== void 0 ? invitees : []); }, [invitees]);
    var currentEmailsKey = currentEmails.join('|');
    var availabilityWindowWithinLimit = useMemo(function () {
        if (!(startTime === null || startTime === void 0 ? void 0 : startTime.isValid()) || !(endTime === null || endTime === void 0 ? void 0 : endTime.isValid()) || !timezone) {
            return false;
        }
        return isAvailabilityWindowWithinLimit({
            startTime: startTime,
            endTime: endTime,
            isAllDayEvent: isAllDayEvent,
            timezone: timezone,
        });
    }, [endTime, isAllDayEvent, startTime, timezone]);
    var timeFetchKey = useMemo(function () {
        if (!(startTime === null || startTime === void 0 ? void 0 : startTime.isValid()) || !(endTime === null || endTime === void 0 ? void 0 : endTime.isValid()) || !timezone) {
            return '';
        }
        var _a = getAvailabilityBounds({
            startTime: startTime,
            endTime: endTime,
            isAllDayEvent: isAllDayEvent,
            timezone: timezone,
        }), from = _a.from, to = _a.to;
        return buildAvailabilityTimeFetchKey({
            from: from,
            to: to,
            recurrenceKey: recurrenceKey,
            timezone: timezone,
            isAllDayEvent: isAllDayEvent,
            composerMode: composerMode,
            eventId: eventId,
            uid: uid,
        });
    }, [
        composerMode,
        endTime,
        eventId,
        isAllDayEvent,
        recurrenceKey,
        startTime,
        timezone,
        uid,
    ]);
    var fetchAvailability = useCallback(function (_a) {
        var currentEmails = _a.currentEmails, timeFetchKey = _a.timeFetchKey, signal = _a.signal;
        var _b = diffInviteeEmails(previousEmailsRef.current, currentEmails), added = _b.added, removed = _b.removed;
        var timeChanged = timeFetchKey !== previousTimeFetchKeyRef.current ||
            forceFullFetchRef.current;
        forceFullFetchRef.current = false;
        if (!timeChanged && added.length === 0 && removed.length > 0) {
            setConflictsByEmail(function (prev) {
                var next = new Map(prev);
                removed.forEach(function (email) { return next.delete(email); });
                return next;
            });
            previousEmailsRef.current = new Set(currentEmails);
            return;
        }
        var emailsToFetch = timeChanged ? currentEmails : added;
        if (emailsToFetch.length === 0) {
            previousTimeFetchKeyRef.current = timeFetchKey;
            previousEmailsRef.current = new Set(currentEmails);
            return;
        }
        var _c = getAvailabilityBounds({
            startTime: startTime,
            endTime: endTime,
            isAllDayEvent: isAllDayEvent,
            timezone: timezone,
        }), from = _c.from, to = _c.to;
        setIsLoading(true);
        var recurrence = recurrenceRule ? [recurrenceRule.toString()] : [];
        fetchInviteesAvailability({
            inviteeEmails: emailsToFetch,
            from: from,
            to: to,
            recurrence: recurrence,
            timeZone: timezone,
            /* @TODO when should we include the remote results? */
            includeRemoteResults: true,
            post: post,
            apiUrl: environmentUrls.apiUrl,
            signal: signal,
        })
            .then(function (conflictsMap) {
            if (signal.aborted)
                return;
            var result = conflictsMap;
            if (composerMode === ComposerMode.EDIT &&
                (eventId || uid) &&
                startTime &&
                endTime) {
                result = filterConflictsForEditingEvent(conflictsMap, {
                    id: eventId,
                    uid: uid,
                    start: startTime.toDate(),
                    end: endTime.toDate(),
                });
            }
            setConflictsByEmail(function (prev) {
                if (timeChanged) {
                    var next_1 = new Map();
                    currentEmails.forEach(function (email) {
                        var _a;
                        next_1.set(email, (_a = result.get(email)) !== null && _a !== void 0 ? _a : []);
                    });
                    return next_1;
                }
                var next = new Map(prev);
                result.forEach(function (conflicts, email) {
                    next.set(email, conflicts);
                });
                removed.forEach(function (email) { return next.delete(email); });
                return next;
            });
            previousTimeFetchKeyRef.current = timeFetchKey;
            previousEmailsRef.current = new Set(currentEmails);
        })
            .catch(function (error) {
            if (isAbortError(error))
                return;
            if (timeChanged) {
                setConflictsByEmail(new Map());
            }
            var errorMessage = error instanceof Error ? error.message : String(error);
            onError(new Error("Error checking invitee availability: ".concat(errorMessage)));
        })
            .finally(function () {
            if (!signal.aborted) {
                setIsLoading(false);
            }
        });
    }, [
        composerMode,
        endTime,
        environmentUrls.apiUrl,
        eventId,
        isAllDayEvent,
        onError,
        post,
        recurrenceRule,
        startTime,
        timezone,
        uid,
    ]);
    useEffect(function () {
        var _a;
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        var shouldSkip = hideInvitees || !timeFetchKey || currentEmails.length === 0;
        var shouldSkipWithLimit = currentEmails.length > MAX_INVITEES_FOR_AVAILABILITY ||
            !availabilityWindowWithinLimit;
        if (shouldSkip || shouldSkipWithLimit) {
            setConflictsByEmail(new Map());
            setIsLoading(false);
            setIsAvailabilitySkipped(shouldSkipWithLimit && !shouldSkip);
            previousTimeFetchKeyRef.current = shouldSkip ? '' : timeFetchKey;
            previousEmailsRef.current = new Set();
            forceFullFetchRef.current = !shouldSkip; // only force on limit-skip
            return;
        }
        setIsAvailabilitySkipped(false);
        var timeUnchanged = timeFetchKey === previousTimeFetchKeyRef.current &&
            !forceFullFetchRef.current;
        var emailsUnchanged = inviteeEmailSetsEqual(previousEmailsRef.current, currentEmails);
        if (timeUnchanged && emailsUnchanged) {
            return;
        }
        var debounceMs = recurrenceKey
            ? AVAILABILITY_RECURRING_DEBOUNCE_MS
            : AVAILABILITY_DEBOUNCE_MS;
        debounceRef.current = setTimeout(function () {
            var controller = new AbortController();
            abortControllerRef.current = controller;
            fetchAvailability({
                currentEmails: currentEmails,
                timeFetchKey: timeFetchKey,
                signal: controller.signal,
            });
        }, debounceMs);
        return function () {
            var _a;
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        };
    }, [
        composerMode,
        currentEmails,
        currentEmailsKey,
        endTime,
        environmentUrls.apiUrl,
        eventId,
        fetchAvailability,
        availabilityWindowWithinLimit,
        hideInvitees,
        isAllDayEvent,
        post,
        onError,
        recurrenceKey,
        startTime,
        timeFetchKey,
        timezone,
        uid,
    ]);
    var inviteesWithAvailability = useMemo(function () {
        return (invitees !== null && invitees !== void 0 ? invitees : []).map(function (invitee) {
            var _a, _b, _c;
            return (__assign(__assign({}, invitee), { conflicts: (_c = conflictsByEmail.get((_b = (_a = invitee.email) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : [] }));
        });
    }, [conflictsByEmail, invitees]);
    return {
        inviteesWithAvailability: inviteesWithAvailability,
        isLoading: isLoading,
        isAvailabilitySkipped: isAvailabilitySkipped,
    };
};
